"use client";

import { useState } from "react";
import { type FieldValues } from "react-hook-form";
import { Tooltip, IconButton, Menu, MenuItem } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { FormDateRangeFields } from "@/components/ui/forms/formDateRangeFieldsComponent/formDateRangeFields.component";
import { presetToRangeDate } from "@/utils/utils";
import type { DATE_PRESETS_TYPE } from "@/utils/constants";
import type { FormDateRangePresetProps } from "./formDateRangePreset.types";
import clsx from "clsx";

const DEFAULT_ACTIVE_SELECTION_COLOR = "var(--mui-palette-warning-main)";

/**
 * Componente que combina um **select de presets** (Hoje, Ontem, Essa semana, etc.)
 * com um **date-range** (De/Até) em uma única linha.
 *
 * Ao selecionar um preset, o range é automaticamente preenchido via `setValue`.
 */
export function FormDateRangePreset<TFieldValues extends FieldValues>({
	control,
	setValue,
	name,
	presetName,
	label,
	startLabel = "De",
	endLabel = "Até",
	presets = [],
	hidePreset = false,
	disabled = false,
	dateRangeDisabled = false,
	dateRangeDisabledTooltip = "Preset 'Todos' selecionado: considera qualquer período para esta data.",
	required,
	displayFormat = "dd/mm/yyyy",
	emptyValue,
	className,
	fieldsContainerClassName,
	presetTooltip = "Selecionar um período pré-definido para a data",
	reserveHelperText = true,
	skipAuxiliaryActionsTab = true,
	fieldSx,
	highlightActiveSelection = true,
	activeHighlightColor = DEFAULT_ACTIVE_SELECTION_COLOR,
}: Readonly<FormDateRangePresetProps<TFieldValues>>) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const menuId = `${String(presetName ?? name)}-preset-menu`;

	const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
		if (disabled) return;
		setAnchorEl(e.currentTarget);
	};

	const handleClose = () => setAnchorEl(null);

	const handleSelectPreset = (preset: string) => {
		if (disabled) return;

		if (presetName)
			setValue(presetName as any, preset as any, {
				shouldDirty: true,
				shouldTouch: true,
			});

		const range = presetToRangeDate(preset as DATE_PRESETS_TYPE);

		if (!range) {
			setValue(name as any, [null, null] as any, {
				shouldDirty: true,
				shouldTouch: true,
			});

			handleClose();
			return;
		}

		setValue(name as any, [range[0], range[1]] as any, {
			shouldDirty: true,
			shouldTouch: true,
		});

		handleClose();
	};

	const presetElement = hidePreset ? undefined : (
		<div className="flex h-8 shrink-0 items-center">
			<Tooltip placement="top-end" arrow title={presetTooltip}>
				<IconButton
					size="small"
					id={`${menuId}-button`}
					aria-controls={anchorEl ? menuId : undefined}
					aria-haspopup="true"
					onClick={handleOpen}
					disabled={disabled}
					tabIndex={disabled || skipAuxiliaryActionsTab ? -1 : undefined}
					sx={{
						border: "1px solid var(--mui-palette-divider)",
						borderRadius: "8px",
						backgroundColor: "var(--mui-palette-background-paper)",
						color: "var(--mui-palette-text-secondary)",
						"&:hover": {
							backgroundColor: "rgb(var(--mui-palette-secondary-mainChannel) / 0.08)",
							color: "var(--mui-palette-secondary-main)",
						},
					}}
				>
					<FuseSvgIcon size={18}>lucide:calendar-sync</FuseSvgIcon>
				</IconButton>
			</Tooltip>

			<Menu
				id={menuId}
				anchorEl={anchorEl}
				open={Boolean(anchorEl) && !disabled}
				onClose={handleClose}
				slotProps={{
					list: {
						"aria-labelledby": `${menuId}-button`,
					},
				}}
			>
				{presets.map((p) => (
					<MenuItem key={p.value} onClick={() => handleSelectPreset(p.value)}>
						{p.label}
					</MenuItem>
				))}
			</Menu>
		</div>
	);

	return (
		<div className={clsx("flex flex-row", className)}>
			<Tooltip title={dateRangeDisabled ? dateRangeDisabledTooltip : ""} placement="top-start" arrow>
				<span className="w-full">
					<FormDateRangeFields<TFieldValues>
						control={control}
						name={name}
						disabled={disabled || dateRangeDisabled}
						geralLabel={label}
						startLabel={startLabel}
						endLabel={endLabel}
						required={required}
						displayFormat={displayFormat}
						emptyValue={emptyValue}
						leadingElement={presetElement}
						fieldsContainerClassName={fieldsContainerClassName}
						reserveHelperText={reserveHelperText}
						skipAuxiliaryActionsTab={skipAuxiliaryActionsTab}
						fieldSx={fieldSx}
						highlightActiveSelection={highlightActiveSelection}
						activeHighlightColor={activeHighlightColor}
					/>
				</span>
			</Tooltip>
		</div>
	);
}
